import { redirect, type MiddlewareFunction } from 'react-router';
import {
  addPersistingQueryParamsToPath,
  getAppPageFromPath,
  getPagePath,
} from '../../helpers/urlHelper';
import { isOperationPage, type OperationPage } from '../../types/app';
import {
  clearSessionData,
  getSavedSession,
  saveUpdatedSessionItem,
  type BokaSession,
} from '../../helpers/sessionHelper';
import { viewContext } from './context';
import { resetSessionState } from './middlewareHelper';

const isPagedChanged = (session: BokaSession, page: OperationPage): boolean =>
  session.page !== page;

const isRequiredSessionDataMissing = (
  session: BokaSession,
  page: OperationPage,
): boolean => {
  const { selected, search } = session;
  const selectedTimeSlot =
    selected.timeSlot || selected.replacementService?.timeSlot;

  if (page === 'services') {
    return !search.postCode;
  }
  if (page === 'timeslots') {
    return !selected.serviceId;
  }
  if (page === 'contact') {
    return !selectedTimeSlot || !selected.serviceId;
  }
  if (page === 'confirm') {
    return (
      !session.bookingCompleted || !selectedTimeSlot || !selected.serviceId
    );
  }
  return false;
};

const isPreBookingPage = (page: OperationPage): boolean =>
  page === 'start' ||
  page === 'services' ||
  page === 'timeslots' ||
  page === 'contact';

let isInitialPageLoad = true;

/**
 * Middleware for routes that reset the full session state (e.g. 404, service-unavailable).
 */
export const resetSessionMiddleware: MiddlewareFunction = async () => {
  resetSessionState();
};

/**
 * Middleware for routes that clear session data only (e.g. questions, questions-confirmed).
 */
export const clearSessionMiddleware: MiddlewareFunction = async () => {
  clearSessionData();
};

/**
 * Middleware for :operation/ routes that validates session state
 * and redirects to the start page when required data is missing.
 */
const operationSessionMiddleware: MiddlewareFunction = async ({
  request,
  params,
  context,
}) => {
  const initialLoad = isInitialPageLoad;
  isInitialPageLoad = false;

  const url = new URL(request.url);
  const page = getAppPageFromPath(url.pathname);

  if (!isOperationPage(page)) {
    return;
  }

  const view = context.get(viewContext);
  const operation =
    view === 'app' && url.pathname.includes('/ikeaapp/')
      ? 'ikeaapp'
      : params.operation;
  const session = getSavedSession();

  if (page === 'start' && initialLoad) {
    resetSessionState();
    return;
  }

  if (!session.operation || session.operation !== operation) {
    resetSessionState();
    if (operation) {
      saveUpdatedSessionItem('operation', operation);
    }
    if (page !== 'start') {
      const redirectPath = operation
        ? getPagePath(operation, view)
        : getPagePath('404', view);
      throw redirect(addPersistingQueryParamsToPath(redirectPath));
    }
    return;
  }

  const hasLocaleChanged =
    CSS_CONFIG.country !== session.locale.country ||
    CSS_CONFIG.language !== session.locale.language;
  if (hasLocaleChanged) {
    resetSessionState();
    saveUpdatedSessionItem('operation', operation);
    if (page !== 'start') {
      const startPagePath = getPagePath(operation, view);
      throw redirect(addPersistingQueryParamsToPath(startPagePath));
    }
    return;
  }

  if (session.bookingCompleted && isPreBookingPage(page)) {
    resetSessionState();
    saveUpdatedSessionItem('operation', operation);
    if (page !== 'start') {
      const startPagePath = getPagePath(operation, view);
      throw redirect(addPersistingQueryParamsToPath(startPagePath));
    }
    return;
  }

  if (isRequiredSessionDataMissing(session, page)) {
    resetSessionState();
    saveUpdatedSessionItem('operation', operation);
    const startPagePath = getPagePath(operation, view);
    throw redirect(addPersistingQueryParamsToPath(startPagePath));
  }

  if (initialLoad && isPagedChanged(session, page)) {
    resetSessionState();
    saveUpdatedSessionItem('operation', operation);
    const startPagePath = getPagePath(operation, view);
    throw redirect(addPersistingQueryParamsToPath(startPagePath));
  }
};

export default operationSessionMiddleware;
